import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';

interface ChatMarkdownRendererProps {
  content: string;
  className?: string;
}

const components: Components = {
  h1: ({ children }) => (
    <h1 className="mt-4 text-base font-semibold leading-6 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mt-4 text-sm font-semibold leading-6 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-3 text-sm font-semibold leading-6 first:mt-0">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="mt-2 whitespace-pre-wrap leading-6 first:mt-0">{children}</p>
  ),
  ul: ({ children }) => <ul className="mt-2 list-disc space-y-1 pl-5 first:mt-0">{children}</ul>,
  ol: ({ children }) => <ol className="mt-2 list-decimal space-y-1 pl-5 first:mt-0">{children}</ol>,
  li: ({ children }) => <li className="leading-6">{children}</li>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-domino-prune underline hover:text-domino-aubergine"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mt-3 border-l-2 border-domino-cardBorder pl-3 text-domino-inkSoft">
      {children}
    </blockquote>
  ),
  pre: ({ children }) => (
    <pre className="mt-3 overflow-x-auto rounded-lg bg-domino-ink p-3 text-xs leading-5 text-domino-warm">
      {children}
    </pre>
  ),
  code: ({ children, className }) => (
    <code
      className={cn(
        'font-mono text-[0.92em]',
        className ? 'text-inherit' : 'rounded bg-black/5 px-1 py-0.5 text-domino-ink'
      )}
    >
      {children}
    </code>
  ),
  table: ({ children }) => (
    <div className="mt-3 overflow-x-auto">
      <table className="min-w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-domino-cardBorder px-2 py-1.5 text-left text-xs font-semibold uppercase text-domino-inkSoft">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-domino-cardBorder/70 px-2 py-1.5 align-top text-sm">
      {children}
    </td>
  ),
};

export const ChatMarkdownRenderer = ({ content, className }: ChatMarkdownRendererProps) => (
  <div className={cn('max-w-none break-words text-sm leading-6 text-domino-ink', className)}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  </div>
);
